import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BsChevronDown } from 'react-icons/bs';
import { RiMenu4Line, RiCloseLine, RiArrowDownSLine } from 'react-icons/ri';
import { FaArrowLeft } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // handleScroll is stable within this effect scope

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const variants = {
    open: { opacity: 1, y: 0 },
    closed: { opacity: 0, y: -20 },
  };

  const mainNavItems = [
    { name: 'Home', href: '#top' },
    { name: 'About Us', href: '#about' },
    { 
      name: 'Investment', 
      href: '#investment',
      dropdown: [
        { name: 'Investment Approach', href: '#investment-approach' },
        { name: 'Investment Criteria', href: '#investment-criteria' },
        { name: 'Investment Stages', href: '#investment-stages' },
      ] 
    },
    { name: 'Our Portfolio', href: '#portfolio' },
    { name: 'Contact Us', href: '#contact' },
  ];
  
  const tradingNavItems = [
    { name: 'Home', href: '#top' },
    { name: 'About', href: '#about' },
    { 
      name: 'Products', 
      href: '#products',
      dropdown: [
        { name: 'EN590 Diesel', href: '#en590' },
        { name: 'Crude Oil', href: '#crude-oil' },
        { name: 'D6 Fuel Oil', href: '#d6-fuel' },
        { name: 'Jet A1 Fuel', href: '#jet-a1' },
      ] 
    },
    { name: 'Trading', href: '#trading' },
    { name: 'Market Insights', href: '#insights' },
    { name: 'Contact', href: '#contact' },
  ];
  
  const navItems = window.location.pathname === '/' ? mainNavItems : tradingNavItems;

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'py-3 bg-black backdrop-blur-lg shadow-lg' : 'py-6 bg-black/50 backdrop-blur-sm'
      }`}
    >
      <div className="container-custom flex justify-between items-center">
        {/* Logo */}
        {window.location.pathname === '/' ? (
          <div className="flex items-center">
            <img 
              src="/images/caishen-capital-logo.svg" 
              alt="Caishen Capital Ltd" 
              className="h-12 md:h-16 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                // Safe fallback without innerHTML
                const fallback = document.createElement('div');
                fallback.className = 'text-4xl font-display font-bold flex items-center';
                const span1 = document.createElement('span');
                span1.className = 'text-accent-gold';
                span1.textContent = 'CAISHEN';
                const span2 = document.createElement('span');
                span2.className = 'text-white ml-1';
                span2.textContent = 'CAPITAL';
                fallback.appendChild(span1);
                fallback.appendChild(span2);
                e.target.parentNode.appendChild(fallback);
              }}
            />
          </div>
        ) : (
          <div className="flex items-center">
            <Link to="/" className="mr-4 text-gray-400 hover:text-white transition-colors">
              <FaArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center">
              <div className="relative">
                <img 
                  src="/images/caishen-trading-logo.svg" 
                  alt="Caishen Trading" 
                  className="h-12 md:h-16 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    // Safe fallback without innerHTML
                    const fallback = document.createElement('div');
                    fallback.className = 'text-4xl font-display font-bold flex items-center';
                    const span1 = document.createElement('span');
                    span1.className = 'text-accent-gold';
                    span1.textContent = 'CAISHEN';
                    const span2 = document.createElement('span');
                    span2.className = 'text-white ml-1';
                    span2.textContent = 'TRADING';
                    fallback.appendChild(span1);
                    fallback.appendChild(span2);
                    e.target.parentNode.appendChild(fallback);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          {navItems.map((item) => (
            <div key={item.name} className="relative group">
              {item.dropdown ? (
                <>
                  <button 
                    className="flex items-center text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      try {
                        if (window.location.pathname === '/') {
                          const element = document.getElementById('investment');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        } else if (window.location.pathname === '/trading') {
                          const element = document.getElementById('products');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }
                      } catch (error) {
                        // Silently handle navigation errors
                      }
                    }}
                  >
                    {item.name}
                    <BsChevronDown className="ml-1 h-4 w-4 group-hover:rotate-180 transition-transform duration-200" />
                  </button>
                  <div className="absolute left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="py-2 glass-card-strong border border-gray-700/50 rounded-xl shadow-xl">
                      {item.dropdown.map((dropdownItem) => (
                        <a
                          key={dropdownItem.href}
                          href={dropdownItem.href}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            try {
                              const targetId = dropdownItem.href.substring(1);
                              const element = document.getElementById(targetId);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth' });
                              }
                            } catch (error) {
                              // Silently handle navigation errors
                            }
                          }}
                        >
                          {dropdownItem.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <a 
                  href={item.href}
                  className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    try {
                      // Special handling for trading page Home link
                      if (item.href === '#top' && window.location.pathname === '/trading') {
                        // Navigate back to main page
                        window.location.href = '/';
                      }
                      // Special handling for Home/top link
                      else if (item.href === '#top') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } else {
                        const targetId = item.href.substring(1);
                        const element = document.getElementById(targetId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    } catch (error) {
                      // Silently handle navigation errors
                    }
                  }}
                >
                  {item.name}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Contact Button */}
        <div className="hidden lg:block">
          <a
            href="#contact"
            className="btn-primary"
          >
            Get in Touch
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMenu}
          className="lg:hidden text-gray-300 hover:text-white"
          aria-label="Toggle Menu"
        >
          {isOpen ? (
            <RiCloseLine className="h-7 w-7" />
          ) : (
            <RiMenu4Line className="h-7 w-7" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={variants}
            className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-gray-800"
          >
            <div className="container-custom py-4">
              <div className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <div key={item.name}>
                    {item.dropdown ? (
                      <div className="py-2">
                        <button 
                          className="block text-sm font-medium text-gray-300 mb-2 hover:text-white transition-colors w-full text-left"
                        >
                          {item.name}
                        </button>
                        <div className="pl-4 border-l border-gray-700/50 space-y-2 mt-2">
                          {item.dropdown.map((dropdownItem) => (
                            <a
                              key={dropdownItem.href}
                              href={dropdownItem.href}
                              className="block text-sm text-gray-400 hover:text-white transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsOpen(false);
                                try {
                                  // Special handling for trading page links
                                  if (dropdownItem.href === '#top' && window.location.pathname === '/trading') {
                                    // Navigate back to main page
                                    window.location.href = '/';
                                  }
                                  // Special handling for top link
                                  else if (dropdownItem.href === '#top') {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  } else {
                                    const targetId = dropdownItem.href.substring(1);
                                    const element = document.getElementById(targetId);
                                    if (element) {
                                      setTimeout(() => {
                                        element.scrollIntoView({ behavior: 'smooth' });
                                      }, 100);
                                    }
                                  }
                                } catch (error) {
                                  // Silently handle navigation errors
                                }
                              }}
                            >
                              {dropdownItem.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <a
                        href={item.href}
                        className="block text-sm font-medium text-gray-300 hover:text-white py-2 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsOpen(false);
                          
                          // Special handling for trading page Home link
                          if (item.href === '#top' && window.location.pathname === '/trading') {
                            // Navigate back to main page
                            window.location.href = '/';
                          }
                          // Special handling for Home/top link
                          else if (item.href === '#top') {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          } else {
                            const targetId = item.href.substring(1);
                            const targetElement = document.getElementById(targetId);
                            if (targetElement) {
                              setTimeout(() => {
                                targetElement.scrollIntoView({ behavior: 'smooth' });
                              }, 100);
                            }
                          }
                        }}
                      >
                        {item.name}
                      </a>
                    )}
                  </div>
                ))}
                <div className="pt-2">
                  <a
                    href="#contact"
                    className="btn-primary w-full flex justify-center py-3"
                    onClick={() => {
                      setIsOpen(false);
                      document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Get in Touch
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;